<template>
  <div class="email-preference">
    <div class="flex preference-description">
      <div class="box grow">
        <h5>Email Notification Preference</h5>
        <p>Configure email notifications you would like to receive from Confluera. Note that these settings are for your account only and doesn't affect other users.</p>
      </div>
      <div class="box">
        <i class="material-icons email">email</i>
      </div>
    </div>
    <div
      class="preference-settings"
      v-if="userEmailNotificationPreferenceStatus === 'saved'"
    >
      <div class="box grow flex column">
        <div class="box grow flex">
          <div class="box title bold">Category</div>
          <div class="box grow">
            <el-checkbox
              v-model="emailPreference.subscribed"
              @change="handleCheckAllChange"
              >{{
                userAccount.getEmailNotificationPreferencesText("subscribed")
              }}</el-checkbox
            >
          </div>
        </div>
        <el-divider></el-divider>
        <div class="box grow flex">
          <div class="box title">
            <span class="bold category-title">Threat Progression</span>
          </div>
          <div class="box grow">
            <div
              class="box"
              v-for="(value, name, index) in emailPreference.trail"
              :key="name"
              :class="{ 'display-none': name.includes('increment') }"
            >
              <el-checkbox
                v-model="emailPreference.trail[name]"
                @change="handlePrefChanges"
                >{{
                  userAccount.getEmailNotificationPreferencesText(name)
                }}</el-checkbox
              >
              <el-divider
                v-if="index !== Object.keys(emailPreference.trail).length - 1"
              ></el-divider>
            </div>
          </div>
        </div>
        <el-divider></el-divider>
        <div class="box grow flex">
          <div class="box title">
            <span class="bold category-title">Host</span>
          </div>
          <div class="box grow">
            <div
              class="box"
              v-for="(value, name, index) in emailPreference.host"
              :key="name"
              :class="{ 'display-none': name.includes('increment') }"
            >
              <el-checkbox
                v-model="emailPreference.host[name]"
                @change="handlePrefChanges"
                >{{
                  userAccount.getEmailNotificationPreferencesText(name)
                }}</el-checkbox
              >
              <el-divider
                v-if="index !== Object.keys(emailPreference.host).length - 1"
              ></el-divider>
            </div>
          </div>
        </div>
        <el-divider></el-divider>
        <div class="box grow flex mb-10">
          <div class="box title bold">Recipients</div>
          <div class="box grow flex">
            Send a copy of the notifications to the following recipients
          </div>
        </div>
        <div class="box grow flex">
          <div class="box title"></div>
          <div class="box grow flex">
            <el-form
              class="box grow flex"
              :model="ccEmailInfo"
              :rules="ccEmailInfoRules"
              ref="ccEmailInfoForm"
            >
              <el-form-item class="box grow" prop="email">
                <el-input
                  autocomplete="off"
                  placeholder="Enter email address of recipients"
                  v-model.trim="ccEmailInfo.email"
                  @keyup.enter.native="
                    addAdditionalRecipientEmail(ccEmailInfo.email)
                  "
                ></el-input>
              </el-form-item>
              <span
                class="box add-button cursor-pointer"
                @click="addAdditionalRecipientEmail(ccEmailInfo.email)"
              >
                <i class="material-icons c-icon">add</i>
              </span>
            </el-form>
          </div>
        </div>
        <div
          class="box grow"
          v-for="(cc, index) in emailPreference.secondary_emails"
          :key="index"
        >
          <div class="box grow flex">
            <div class="box title"></div>
            <div class="box grow flex">
              <div class="box grow">
                <div v-if="cc.email">{{ cc.email }}</div>
                <div v-else></div>
              </div>
              <div class="box grow edit-icon">
                <i
                  class="material-icons c-icon float-right cursor-pointer"
                  @click="emailPreference.secondary_emails.splice(index, 1)"
                  >clear</i
                >
              </div>
            </div>
          </div>
          <div class="box grow flex">
            <div class="box title"></div>
            <div class="box grow flex">
              <el-divider></el-divider>
            </div>
          </div>
        </div>
      </div>
    </div>
    <el-divider></el-divider>
    <div class="box grow flex float-right footer">
      <el-button
        type="text"
        class="icon-text-button"
        @click="udpateEmailNotificationPreferences()"
      >
        <i class="material-icons c-icon">save</i>
        <span>Save</span>
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  EMAIL_NOTIFICATION_PREFERENCE_REQUEST,
  EMAIL_NOTIFICATION_PREFERENCE_UPDATE_REQUEST
} from "@/store/modules/user/email-preference.type";
import { emailRe } from "@/utilities/regex/email";

export default {
  name: "EmailPreference",
  data() {
    return {
      emailPreference: {},
      ccEmailInfo: {
        email: ""
      },
      ccEmailInfoRules: {
        email: [
          {
            required: true,
            trigger: "blur",
            validator: (rule, value, callback) => {
              if (value === "") {
                callback(new Error("Please input the user's email address"));
              } else {
                emailRe.test(value)
                  ? callback()
                  : callback(new Error("Invalid email format"));
              }
            }
          }
        ]
      }
    };
  },
  methods: {
    handleCheckAllChange(val) {
      let prefs = ["trail", "host"];
      for (let prefVal in prefs) {
        this.checkPreferenceObj(this.emailPreference, prefs[prefVal], val);
      }
    },
    checkPreferenceObj(preferences, prefKey, val) {
      for (let key in preferences[prefKey]) {
        if (key.includes("increment")) {
          continue;
        }
        preferences[prefKey][key] = val;
      }
    },
    handlePrefChanges(val) {
      // if all preferences are unchecked, unsubscribed to email notification
      let prefs = ["trail", "host"];
      let toSubscribe = false;
      for (let prefVal in prefs) {
        toSubscribe = this.anyPreferenceChecked(
          this.emailPreference,
          prefs[prefVal]
        );
        if (toSubscribe) {
          this.emailPreference.subscribed = toSubscribe;
          return true;
        }
      }
      this.emailPreference.subscribed = toSubscribe;
    },
    anyPreferenceChecked(preferences, prefKey) {
      for (let pref in preferences[prefKey]) {
        if (pref.includes("increment")) {
          continue;
        }
        if (preferences[prefKey][pref]) {
          return true;
        }
      }
      return false;
    },
    addAdditionalRecipientEmail(ccEmail) {
      this.$refs["ccEmailInfoForm"].validate(valid => {
        if (valid) {
          this.emailPreference.secondary_emails.push({ email: ccEmail });
          this.$refs["ccEmailInfoForm"].clearValidate();
          this.$refs["ccEmailInfoForm"].resetFields();
        }
      });
    },
    getEmailNotificationPreferences() {
      this.$store
        .dispatch(EMAIL_NOTIFICATION_PREFERENCE_REQUEST)
        .then(preferences => {
          this.emailPreference = _.cloneDeep(
            _.omit(this.userAccount.notificationEmailPreferences, "user_acct")
          );
        });
    },
    udpateEmailNotificationPreferences() {
      let ccEmails = [];
      if (!this.emailPreference.hasOwnProperty("secondary_emails")) {
        this.emailPreference.secondary_emails = [];
      }
      if (this.emailPreference.secondary_emails.length > 0) {
        ccEmails = _.map(this.emailPreference.secondary_emails, "email");
      }

      this.$store
        .dispatch(EMAIL_NOTIFICATION_PREFERENCE_UPDATE_REQUEST, {
          subscribed: this.emailPreference.subscribed,
          hostRiskChange: this.emailPreference.host.host_risk_change,
          hostStatusChange: this.emailPreference.host.host_status_change,
          trailAssignmentChange: this.emailPreference.trail
            .trail_assignment_change,
          trailProgression: this.emailPreference.trail.trail_progression,
          trailRiskChange: this.emailPreference.trail.trail_risk_change,
          trailStatusChange: this.emailPreference.trail.trail_status_change,
          secondaryEmails: ccEmails
        })
        .then(
          preferences => {
            this.$message({
              type: "success",
              message: "Email notification preferences were successfully saved."
            });
          },
          errMsg => {
            this.$message({
              type: "error",
              message: `Unable to save email notification preferences.`
            });
          }
        );
    }
  },
  created() {
    this.getEmailNotificationPreferences();
  },
  computed: {
    ...mapGetters([
      "userEmailNotificationPreferenceStatus",
      "userEmailNotificationPreferenceUpdateStatus",
      "userEmailNotificationPreference",
      "userAccount"
    ])
  },
  components: {}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
@import "assets/styles/_variables.css";

.preference-description {
  > .box:first-of-type {
    margin-right: 50px;
  }
}

.preference-settings {
  overflow: scroll;
  height: calc(100vh-310px);

  .title {
    min-width: 200px;
  }
}

.email {
  font-size: 6em;
}

.category-title {
  float: right;
  margin-right: 30px;
}

.icon-text-button {
  background: var(--main-blue);
  padding: 5px 10px;

  span {
    .c-icon {
      color: white;
      font-size: 1.2em;
      vertical-align: text-bottom;
      margin-right: 5px;
    }
    span {
      color: white;
      margin-bottom: 6px;
    }
  }
}

.c-icon {
  border: 1px solid;
  border-radius: 50%;
}

.add-button {
  vertical-align: middle;
  line-height: 50px;
  margin-left: 5px;
}
</style>



// WEBPACK FOOTER //
// src/views/admin/EmailPreference.vue